/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Progress} from '@chakra-ui/react'
import {
    Box,
    Button,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link} from 'react-router-dom'
import {calculateCountMap, updateCountMap, getMaterialGroups} from '../../../utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useHandleEmployeeBalance} from '../../../custom-hooks/use-handle-employee-balance'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import LoadingSpinner from '../../../components/loading-spinner'
import SanitizedHtml from '../../../components/html-sanitizer'
import {useGetEmployeeEditorial} from '../../../custom-api/hooks/use-get-employee-editorial'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../utils/contentstack-utils'

const EmployeeCategorySalesBalance = ({employeeOrderSummary, employeeBalancePrefData}) => {
    const materialGroups = useMemo(() => {
        if (!employeeBalancePrefData?.products) return []

        const countMap = calculateCountMap(employeeBalancePrefData.products)

        if (employeeOrderSummary?.response?.records) {
            updateCountMap(countMap, employeeOrderSummary?.response?.records)
        }

        return getMaterialGroups(employeeBalancePrefData.products, countMap)
    }, [employeeOrderSummary, employeeBalancePrefData])

    return (
        <Flex direction="column" gap={2}>
            {materialGroups.length > 0 &&
                materialGroups.map(({materialGroup, displayValue}, index) => (
                    <Box key={index}>
                        {materialGroup}: {displayValue}
                    </Box>
                ))}
        </Flex>
    )
}

EmployeeCategorySalesBalance.propTypes = {
    employeeOrderSummary: PropTypes.object,
    employeeBalancePrefData: PropTypes.object,
}

const EmployeeBalance = () => {
    const styles = useMultiStyleConfig('DashboardComponents')
    const {currency} = useCurrency()
    const intl = useIntl()

    const {data: customer} = useCurrentCustomer()
    const [isLoading, setIsLoading] = useState(!customer?.c_isEmployeeVip)

    const {handleEmployeeBalance, employeeBalancePrefData, employeeError, employeeData} =
        useHandleEmployeeBalance()
    const [spent, setSpent] = useState(0)
    const [total, setTotal] = useState(1500)
    const [percentage, setPercentage] = useState(0)
    /**
     * Fetch employee balance from OMS
     */
    useEffect(() => {
        if (customer?.c_isEmployeeVip) {
            return
        }
        const fetchEmployeeBalance = async () => {
            const result = await handleEmployeeBalance()
            if (result) {
                setSpent(result.totalAmountWithTax || 0)
                setTotal(parseFloat(employeeBalancePrefData?.balance || 0) || 1500)
                setPercentage(
                    ((result.totalAmountWithTax || 0) /
                        (employeeBalancePrefData?.balance || 1500)) *
                        100,
                )
                setIsLoading(false)
            }
        }

        fetchEmployeeBalance()
    }, [customer.c_gigyaId && customer.email])

    const listPriceText = Number.isInteger(total)
        ? intl.formatNumber(total, {
              style: 'currency',
              currency,
              minimumFractionDigits: 0, // No decimals for integers
              maximumFractionDigits: 0,
          })
        : intl.formatNumber(total, {
              style: 'currency',
              currency,
          })

    const spentPriceText = Number.isInteger(spent)
        ? intl.formatNumber(spent, {
              style: 'currency',
              currency,
              minimumFractionDigits: 0, // No decimals for integers
              maximumFractionDigits: 0,
          })
        : intl.formatNumber(spent, {
              style: 'currency',
              currency,
          })

    const {data: employeeEditorialData} = useGetEmployeeEditorial()
    const {buildUrl, locale} = useMultiSite()
    const categoryData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids:
                    employeeEditorialData?.button?.productsfcc?.data?.length > 0
                        ? employeeEditorialData?.button.productsfcc.data[0].id
                        : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'oms-employee-index',
            },
        },
        {
            enabled: employeeEditorialData?.button?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: employeeEditorialData?.button,
        categoryData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    return (
        <Box data-testid="employee-sales-component" {...styles.employeeContainer}>
            {isLoading && <LoadingSpinner />}
            {/* show only if we have data from OMS and custom preference */}
            {!customer?.c_isEmployeeVip && !isLoading && (
                <Flex flexDir={'column'} gap={6}>
                    <Text as="h3" tabIndex="0" {...styles.employeeTitle}>
                        <FormattedMessage
                            id="account_detail.title.account_employee_sales"
                            defaultMessage="Employee sales details"
                        />
                    </Text>
                    <Text {...styles.employeeDescription}>
                        <FormattedMessage
                            id="account_detail.description.account_employee_sales"
                            defaultMessage="As an employee of the De'Longhi Group, you have a maximum amount available for purchasing discounted products. Below you will find your current balance"
                        />
                    </Text>
                    {employeeData && employeeBalancePrefData && (
                        <Flex flexDir={'column'} gap={6}>
                            <Box>
                                <Text fontWeight="bold">
                                    {spentPriceText} / {listPriceText}{' '}
                                    {intl.formatMessage({
                                        id: 'account_detail.account_employee_total',
                                        defaultMessage: 'Spent',
                                    })}
                                </Text>
                                <Progress
                                    value={percentage}
                                    size="md"
                                    sx={{
                                        '& div[role="progressbar"]': {
                                            backgroundColor: 'black',
                                        },
                                    }}
                                    borderRadius="md"
                                    maxW={'259px'}
                                />
                            </Box>
                            <Box>
                                {''}
                                <EmployeeCategorySalesBalance
                                    employeeOrderSummary={employeeData}
                                    employeeBalancePrefData={employeeBalancePrefData}
                                />
                            </Box>
                        </Flex>
                    )}
                    {employeeError && (
                        <Flex justify="center" mb={4}>
                            <Text color="red.500" fontWeight="bold">
                                {employeeError}
                            </Text>
                        </Flex>
                    )}
                    {label && url && (
                        <Button {...buttonProps} {...styles.employeeCta} variant={'primary'}>
                            {label}
                        </Button>
                    )}
                    {employeeEditorialData?.body_copy && (
                        <SanitizedHtml as={Box} html={employeeEditorialData.body_copy} />
                    )}
                </Flex>
            )}
            {customer?.c_isEmployeeVip && label && url && (
                <Flex flexDir={'column'} gap={6}>
                    <Button {...buttonProps} {...styles.employeeCta} variant={'primary'}>
                        {label}
                    </Button>
                </Flex>
            )}
        </Box>
    )
}

EmployeeBalance.getTemplateName = () => 'employee-balance'

EmployeeBalance.propTypes = {
    match: PropTypes.object,
}

export default EmployeeBalance
